import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * Top 10 most-logged meal items this month, derived from the `meal.foods`
 * jsonb array. Names are bucketed lower-case + trimmed so casual variants
 * like "banana" / "Banana" / "banana " merge.
 */

type FoodEntry = { name?: string; kcal?: number };

export function TopFoodsThisMonth() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["top_foods_month", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const since = new Date();
      since.setDate(1);
      since.setHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("meal")
        .select("foods")
        .eq("user_id", userId!)
        .gte("ts", since.toISOString())
        .limit(500);
      if (error) throw error;
      const byName = new Map<string, { name: string; count: number; kcal: number }>();
      for (const row of data ?? []) {
        const foods = (row as { foods?: unknown }).foods;
        if (!Array.isArray(foods)) continue;
        for (const f of foods as FoodEntry[]) {
          if (!f?.name) continue;
          const key = f.name.trim().toLowerCase();
          const cur = byName.get(key) ?? { name: f.name, count: 0, kcal: 0 };
          cur.count++;
          cur.kcal += Number(f.kcal ?? 0);
          byName.set(key, cur);
        }
      }
      return [...byName.values()].sort((a, b) => b.count - a.count).slice(0, 10);
    },
  });

  if (q.isLoading) return <SkeletonLoader variant="card" height={200} />;
  if (!q.data || q.data.length === 0) {
    return (
      <article style={cardStyle}>
        <EmptyState headline="No food data this month yet." sub="Log a few meals to surface your repeats." />
      </article>
    );
  }

  return (
    <article style={cardStyle}>
      <h3 style={{ margin: 0, marginBottom: 12, fontSize: "var(--type-body)", fontWeight: 500 }}>
        Top foods · this month
      </h3>
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
        {q.data.map((f, i) => (
          <li
            key={f.name + i}
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto",
              alignItems: "baseline",
              gap: 12,
              padding: "8px 0",
              borderBottom: "1px solid var(--hair)",
              fontSize: "var(--type-body-s)",
            }}
          >
            <span className="mono" style={{ color: "var(--fg-3)", width: 20 }}>
              {i + 1}.
            </span>
            <span>{f.name}</span>
            <span className="mono" style={{ color: "var(--fg-2)" }}>×{f.count}</span>
            <span className="mono" style={{ color: "var(--fg-3)" }}>{Math.round(f.kcal)} kcal</span>
          </li>
        ))}
      </ol>
    </article>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--bg-2)",
  border: "1px solid var(--hair)",
  borderRadius: "var(--radius-card)",
  padding: "var(--space-5)",
};
